// /campaigns/dashboard — campaigns you launched (funding, budget left, edit,
// clip review queue + spend) and the ones you joined (with your earnings).
// /campaigns/funded is Stripe's success page.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiCheck, FiTrash2 } from "react-icons/fi";
import { axiosInstance } from "../../config";
import { CampaignsShell, briefPath, budgetLeft, copyText, fmtMoney, isPool, payLabel } from "./campaignsShared";
import { EarningsSummary, ReviewPanel } from "./SubmitClip";

const STATUS_LABEL = {
  active: "Live",
  pending_funding: "Awaiting funding",
  paused: "Paused",
  completed: "Completed",
  draft: "Draft",
};

function Row({ c, onFund, onDelete }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const live = c.status !== "pending_funding" && c.status !== "draft";
  const { total, left, pct } = budgetLeft(c);
  const url = `${window.location.origin}${briefPath(c)}`;
  return (
    <div className="cmp-dash-row">
      <div className="cmp-dash-avatar">
        {c.brandLogo ? <img src={c.brandLogo} alt="" /> : <span>{(c.streamerName || c.brandName)?.[0]?.toUpperCase()}</span>}
      </div>
      <div className="cmp-dash-main">
        <div className="cmp-dash-title">
          {c.title}
          <span className={`cmp-pill ${c.status}`}>{STATUS_LABEL[c.status] || c.status}</span>
          {c.funding?.testMode && <span className="cmp-pill">Test</span>}
        </div>
        {isPool(c) ? (
          <div className="cmp-dash-meta">{payLabel(c)}{c.status === "active" && c.expiresAt ? ` · ends ${new Date(c.expiresAt).toLocaleDateString()}` : ""}</div>
        ) : (
          <>
            <div className="cmp-dash-meta">{payLabel(c)} · {fmtMoney(left)} of {fmtMoney(total)} left</div>
            <div className="cmp-meter"><span style={{ width: `${pct}%` }} /></div>
          </>
        )}
      </div>
      <div className="cmp-dash-actions">
        {c.status === "pending_funding"
          ? <button className="cmp-btn sm" onClick={() => onFund(c)}>Fund {c.funding?.pool ? fmtMoney(c.funding.pool) : ""}</button>
          : <button className="cmp-btn sm ghost" onClick={() => copyText(url, "Brief link copied")}>Copy link</button>}
        <button className="cmp-btn sm ghost" onClick={() => navigate(briefPath(c))}>View</button>
        <button className="cmp-btn sm ghost" onClick={() => navigate(`/campaigns/launch?edit=${c._id}`)}>Edit</button>
        {(c.funding?.status !== "paid" || c.funding?.testMode) && (
          <button className="cmp-btn sm ghost" onClick={() => onDelete(c)} aria-label={`Delete ${c.title}`}><FiTrash2 aria-hidden="true" /> Delete</button>
        )}
        {live && <button className={`cmp-btn sm${open ? "" : " ghost"}`} onClick={() => setOpen((v) => !v)}>Submissions</button>}
      </div>
      {open && <div className="cmp-dash-review"><ReviewPanel campaign={c} /></div>}
    </div>
  );
}

export default function CampaignDashboardPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [mine, setMine] = useState(null);
  const [joined, setJoined] = useState([]);

  useEffect(() => {
    if (sp.get("funding") === "cancelled") toast("Checkout cancelled. Your campaign is saved, fund it any time.");
    axiosInstance.get("/campaigns/mine").then((r) => setMine(r.data.campaigns || [])).catch(() => setMine([]));
    axiosInstance.get("/campaigns/applied").then((r) => setJoined((r.data.applications || []).map((a) => a.campaign).filter(Boolean))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.title}? This can't be undone.`)) return;
    try {
      await axiosInstance.delete(`/campaigns/${c._id}`);
      setMine((list) => list.filter((x) => x._id !== c._id));
      toast.success("Campaign deleted");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't delete it. Try again.");
    }
  };

  const fund = async (c) => {
    try {
      const r = await axiosInstance.post(`/campaigns/${c._id}/checkout`);
      window.location.href = r.data.checkoutUrl;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't start checkout");
    }
  };

  return (
    <CampaignsShell>
      <div className="cmp-page-head row">
        <div>
          <h1>Campaign dashboard</h1>
          <p>Your campaigns and the ones you're clipping for.</p>
        </div>
        <button className="cmp-btn" onClick={() => navigate("/campaigns/launch")}>Launch campaign</button>
      </div>

      <h2 className="cmp-dash-h">Launched by you</h2>
      {mine === null ? <div className="cmp-loading">Loading…</div>
        : mine.length === 0 ? (
          <div className="cmp-empty small">
            <p>You haven't launched a campaign yet.</p>
            <button className="cmp-btn" onClick={() => navigate("/campaigns/launch")}>Launch your first campaign</button>
          </div>
        ) : <div className="cmp-dash-list">{mine.map((c) => <Row key={c._id} c={c} onFund={fund} onDelete={remove} />)}</div>}

      <EarningsSummary />

      <h2 className="cmp-dash-h">Clipping for</h2>
      {joined.length === 0 ? (
        <div className="cmp-empty small">
          <p>Find a campaign, read the brief and start clipping.</p>
          <button className="cmp-btn ghost" onClick={() => navigate("/campaigns")}>Discover campaigns</button>
        </div>
      ) : (
        <div className="cmp-dash-list">
          {joined.map((c) => (
            <button key={c._id} className="cmp-dash-row link" onClick={() => navigate(briefPath(c))}>
              <div className="cmp-dash-avatar">{c.brandLogo ? <img src={c.brandLogo} alt="" /> : <span>{c.brandName?.[0]?.toUpperCase()}</span>}</div>
              <div className="cmp-dash-main">
                <div className="cmp-dash-title">{c.title}</div>
                <div className="cmp-dash-meta">{c.brandName} · {payLabel(c)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </CampaignsShell>
  );
}

// Stripe success_url: confirm payment (webhook may lag a few seconds) and
// send them to the live brief.
export function CampaignFundedPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [state, setState] = useState({ status: "checking", campaign: null });

  useEffect(() => {
    const id = sp.get("session_id");
    if (!id) { setState({ status: "error" }); return undefined; }
    let tries = 0;
    let timer;
    const check = async () => {
      tries += 1;
      try {
        const r = await axiosInstance.get("/campaigns/funding/verify", { params: { session_id: id } });
        if (r.data.paid) return setState({ status: "paid", campaign: r.data.campaign });
        if (tries < 10) timer = setTimeout(check, 2000);
        else setState({ status: "pending", campaign: r.data.campaign });
      } catch {
        setState({ status: "error" });
      }
    };
    check();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const c = state.campaign;
  return (
    <CampaignsShell>
      <div className="cmp-empty">
        {state.status === "checking" && <><h2>Confirming your payment…</h2><p>This takes a few seconds.</p></>}
        {state.status === "paid" && c && (
          <>
            <div className="cmp-funded-mark"><FiCheck /></div>
            <h2>{c.title} is live</h2>
            <p>{fmtMoney(c.funding?.pool || c.totalBudget)} is in the clipper pool. Share the brief so clippers can start.</p>
            <div className="cmp-empty-actions">
              <button className="cmp-btn" onClick={() => navigate(briefPath(c))}>Open the brief</button>
              <button className="cmp-btn ghost" onClick={() => copyText(`${window.location.origin}${briefPath(c)}`, "Brief link copied")}>Copy link</button>
            </div>
          </>
        )}
        {state.status === "pending" && (
          <><h2>Payment processing</h2><p>Stripe hasn't confirmed it yet. Your campaign goes live as soon as it does.</p>
            <button className="cmp-btn" onClick={() => navigate("/campaigns/dashboard")}>Go to dashboard</button></>
        )}
        {state.status === "error" && (
          <><h2>Couldn't confirm payment</h2><p>If you were charged, your campaign will go live shortly. Check the dashboard.</p>
            <button className="cmp-btn" onClick={() => navigate("/campaigns/dashboard")}>Go to dashboard</button></>
        )}
      </div>
    </CampaignsShell>
  );
}
